import { cloneElement, FC, Fragment, HTMLAttributes, ReactElement, ThHTMLAttributes, useRef } from "react";
import { Header } from "@tanstack/react-table";
import classNames from "classnames";
import { Tooltip } from "../../../Tooltip";
import { renderColumnHeader } from "../../utils";

import { ReactComponent as ArrowDown } from "./icons/arrow-down.svg";
import { ReactComponent as ArrowUp } from "./icons/arrow-up.svg";

import styles from "./styles.module.scss";

export type HeaderCellProps = ThHTMLAttributes<HTMLTableCellElement> & {
  header?: Header<object, unknown>,
  hideTooltip?: boolean,
  textClassname?: string
}

export const HeaderCell: FC<HeaderCellProps> = (({ header, hideTooltip, textClassname, ...props }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const columnHeader = header ? renderColumnHeader(header.column, header.getContext()) as ReactElement : <></>;

  const {
    header:headerPropsHeader,
    hideTooltip: headerPropsHideTooltip,
    textClassname: headerPropsTextClassname,
    ...headerProps
  } = header?.column?.columnDef.meta?.getHeaderProps?.(header) || {};

  return (
    <th
      {...props}
      {...headerProps}
      style={{
        ...props?.style,
        ...headerProps?.style,
      }}
      className={classNames(styles.root, headerProps?.className, props.className)}
    >
      {
        cloneElement(
          columnHeader,
          {
            className: classNames(
              columnHeader.props.className,
              styles.title,
              {
                [styles.sortable]: header?.column?.getCanSort?.(),
              },
            ),
            onClick: header?.column?.getToggleSortingHandler?.(),
          } as HTMLAttributes<HTMLDivElement>,
          <>
            <Tooltip
              disabled={hideTooltip || headerPropsHideTooltip || (divRef?.current?.clientWidth || 0) >= (divRef?.current?.scrollWidth || 0)}
              supportingContent={columnHeader.props.children}
              placement={"top"}
            >
              <div
                ref={divRef}
                className={classNames(styles.text, textClassname, headerPropsTextClassname)}
              >
                {columnHeader.props.children}
              </div>
            </Tooltip>
            {
              header?.column?.getCanSort?.() &&
              <div className={styles.sortIcon}>
                {{
                  asc: <ArrowUp />,
                  desc: <ArrowDown />,
                }[header?.column?.getIsSorted?.() as string] ?? null}
              </div>
            }
          </>,
        )
      }
      {
        header?.column?.getCanResize?.() &&
        <div
          className={classNames(styles.resizer, { [styles.isResizing]: header.column.getIsResizing() })}
          onDoubleClick={() => header.column.resetSize()}
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
        />
      }
    </th>
  );
});